import React, { useState, useRef } from 'react';
import { ArrowLeft, CheckCircle, Edit2, AlertTriangle, Circle, Square, Check, X, Loader2, Sparkles } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
import { InternalNotesButton } from './InternalNotesButton';
interface Rental {
  id: string;
  name: string;
  eventName: string;
  proposedEventDate: string;
  assignedTechnician?: string;
  invoiceId?: string;
  internalNotes?: string;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
  };
}
interface OnsitePrepProps {
  rentals: Rental[];
  onNotesUpdated?: () => void;
}
interface EquipmentCount {
  expected: number;
  actual: number;
}
interface PrepChecklist {
  redChest0: EquipmentCount;
  blueChest0: EquipmentCount;
  redChest1: EquipmentCount;
  blueChest1: EquipmentCount;
  redChest2: EquipmentCount;
  blueChest2: EquipmentCount;
  redChest3: EquipmentCount;
  blueChest3: EquipmentCount;
  redChest4: EquipmentCount;
  blueChest4: EquipmentCount;
  redChest5: EquipmentCount;
  blueChest5: EquipmentCount;
  redHeadXS: EquipmentCount;
  blueHeadXS: EquipmentCount;
  redHeadS: EquipmentCount;
  blueHeadS: EquipmentCount;
  redHeadM: EquipmentCount;
  blueHeadM: EquipmentCount;
  redHeadL: EquipmentCount;
  blueHeadL: EquipmentCount;
  redHeadXL: EquipmentCount;
  blueHeadXL: EquipmentCount;
}
export function OnsitePrep({
  rentals,
  onNotesUpdated
}: OnsitePrepProps) {
  const [selectedEvent, setSelectedEvent] = useState<Rental | null>(null);
  const [checklist, setChecklist] = useState<PrepChecklist | null>(null);
  const [signature, setSignature] = useState<string>('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [existingRecordId, setExistingRecordId] = useState<string | null>(null);
  const [prepFilter, setPrepFilter] = useState<'future' | 'past'>('future');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const initializeChecklist = async (rental: Rental) => {
    setSelectedEvent(rental);
    setIsLoadingData(true);
    try {
      // Load pre-event checklist to get expected values
      const preEventData = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Pre_Event_Checklist_Report',
        criteria: `Gen_3_Scoring_Rental == ${rental.id}`
      };
      const preEventChecklists = await callWFAPI(0, 'zohoCreator', preEventData);
      const preEventChecklist = preEventChecklists[0];
      // Set expected values from pre-event checklist, or 0 if not found
      const getExpectedValue = (fieldName: string): number => {
        if (!preEventChecklist) return 0;
        return parseInt(preEventChecklist[fieldName]) || 0;
      };
      // Try to load existing equipment count from database
      const data = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Equipment_Count_Report',
        criteria: `Gen_3_Scoring_Rental == ${rental.id}`
      };
      const equipCounts = await callWFAPI(0, 'zohoCreator', data);
      const existingCount = equipCounts[0];
      const expectedCount = rental.selections.fullGen3Ring || 1;
      if (existingCount) {
        // Load existing data
        setExistingRecordId(existingCount.ID);
        const loadedChecklist: PrepChecklist = {
          redChest0: {
            expected: getExpectedValue('Red_Chest_0'),
            actual: parseInt(existingCount.Red_Chest_0) || 0
          },
          blueChest0: {
            expected: getExpectedValue('Blue_Chest_0'),
            actual: parseInt(existingCount.Blue_Chest_0) || 0
          },
          redChest1: {
            expected: getExpectedValue('Red_Chest_1'),
            actual: parseInt(existingCount.Red_Chest_1) || 0
          },
          blueChest1: {
            expected: getExpectedValue('Blue_Chest_1'),
            actual: parseInt(existingCount.Blue_Chest_1) || 0
          },
          redChest2: {
            expected: getExpectedValue('Red_Chest_2'),
            actual: parseInt(existingCount.Red_Chest_2) || 0
          },
          blueChest2: {
            expected: getExpectedValue('Blue_Chest_2'),
            actual: parseInt(existingCount.Blue_Chest_2) || 0
          },
          redChest3: {
            expected: getExpectedValue('Red_Chest_3'),
            actual: parseInt(existingCount.Red_Chest_3) || 0
          },
          blueChest3: {
            expected: getExpectedValue('Blue_Chest_3'),
            actual: parseInt(existingCount.Blue_Chest_3) || 0
          },
          redChest4: {
            expected: getExpectedValue('Red_Chest_4'),
            actual: parseInt(existingCount.Red_Chest_4) || 0
          },
          blueChest4: {
            expected: getExpectedValue('Blue_Chest_4'),
            actual: parseInt(existingCount.Blue_Chest_4) || 0
          },
          redChest5: {
            expected: getExpectedValue('Red_Chest_5'),
            actual: parseInt(existingCount.Red_Chest_5) || 0
          },
          blueChest5: {
            expected: getExpectedValue('Blue_Chest_5'),
            actual: parseInt(existingCount.Blue_Chest_5) || 0
          },
          redHeadXS: {
            expected: getExpectedValue('Red_Head_XS'),
            actual: parseInt(existingCount.Red_Head_XS) || 0
          },
          blueHeadXS: {
            expected: getExpectedValue('Blue_Head_XS'),
            actual: parseInt(existingCount.Blue_Head_XS) || 0
          },
          redHeadS: {
            expected: getExpectedValue('Red_Head_S'),
            actual: parseInt(existingCount.Red_Head_S) || 0
          },
          blueHeadS: {
            expected: getExpectedValue('Blue_Head_S'),
            actual: parseInt(existingCount.Blue_Head_S) || 0
          },
          redHeadM: {
            expected: getExpectedValue('Red_Head_M'),
            actual: parseInt(existingCount.Red_Head_M) || 0
          },
          blueHeadM: {
            expected: getExpectedValue('Blue_Head_M'),
            actual: parseInt(existingCount.Blue_Head_M) || 0
          },
          redHeadL: {
            expected: getExpectedValue('Red_Head_L'),
            actual: parseInt(existingCount.Red_Head_L) || 0
          },
          blueHeadL: {
            expected: getExpectedValue('Blue_Head_L'),
            actual: parseInt(existingCount.Blue_Head_L) || 0
          },
          redHeadXL: {
            expected: getExpectedValue('Red_Head_XL'),
            actual: parseInt(existingCount.Red_Head_XL) || 0
          },
          blueHeadXL: {
            expected: getExpectedValue('Blue_Head_XL'),
            actual: parseInt(existingCount.Blue_Head_XL) || 0
          }
        };
        setChecklist(loadedChecklist);
        // Load signature
        if (existingCount.Signature) {
          setSignature(existingCount.Signature);
          // Draw signature on canvas
          const canvas = canvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              const img = new Image();
              img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              };
              img.src = existingCount.Signature;
            }
          }
        }
        // Set to edit mode
        setIsSaved(true);
      } else {
        // No existing data - create new checklist
        setExistingRecordId(null);
        const newChecklist: PrepChecklist = {
          redChest0: {
            expected: getExpectedValue('Red_Chest_0'),
            actual: 0
          },
          blueChest0: {
            expected: getExpectedValue('Blue_Chest_0'),
            actual: 0
          },
          redChest1: {
            expected: getExpectedValue('Red_Chest_1'),
            actual: 0
          },
          blueChest1: {
            expected: getExpectedValue('Blue_Chest_1'),
            actual: 0
          },
          redChest2: {
            expected: getExpectedValue('Red_Chest_2'),
            actual: 0
          },
          blueChest2: {
            expected: getExpectedValue('Blue_Chest_2'),
            actual: 0
          },
          redChest3: {
            expected: getExpectedValue('Red_Chest_3'),
            actual: 0
          },
          blueChest3: {
            expected: getExpectedValue('Blue_Chest_3'),
            actual: 0
          },
          redChest4: {
            expected: getExpectedValue('Red_Chest_4'),
            actual: 0
          },
          blueChest4: {
            expected: getExpectedValue('Blue_Chest_4'),
            actual: 0
          },
          redChest5: {
            expected: getExpectedValue('Red_Chest_5'),
            actual: 0
          },
          blueChest5: {
            expected: getExpectedValue('Blue_Chest_5'),
            actual: 0
          },
          redHeadXS: {
            expected: getExpectedValue('Red_Head_XS'),
            actual: 0
          },
          blueHeadXS: {
            expected: getExpectedValue('Blue_Head_XS'),
            actual: 0
          },
          redHeadS: {
            expected: getExpectedValue('Red_Head_S'),
            actual: 0
          },
          blueHeadS: {
            expected: getExpectedValue('Blue_Head_S'),
            actual: 0
          },
          redHeadM: {
            expected: getExpectedValue('Red_Head_M'),
            actual: 0
          },
          blueHeadM: {
            expected: getExpectedValue('Blue_Head_M'),
            actual: 0
          },
          redHeadL: {
            expected: getExpectedValue('Red_Head_L'),
            actual: 0
          },
          blueHeadL: {
            expected: getExpectedValue('Blue_Head_L'),
            actual: 0
          },
          redHeadXL: {
            expected: getExpectedValue('Red_Head_XL'),
            actual: 0
          },
          blueHeadXL: {
            expected: getExpectedValue('Blue_Head_XL'),
            actual: 0
          }
        };
        setChecklist(newChecklist);
        setIsSaved(false);
        setSignature('');
      }
    } catch (error) {
      console.error('Error loading equipment count:', error);
      // Initialize with empty checklist on error (all expected values = 0)
      const newChecklist: PrepChecklist = {
        redChest0: {
          expected: 0,
          actual: 0
        },
        blueChest0: {
          expected: 0,
          actual: 0
        },
        redChest1: {
          expected: 0,
          actual: 0
        },
        blueChest1: {
          expected: 0,
          actual: 0
        },
        redChest2: {
          expected: 0,
          actual: 0
        },
        blueChest2: {
          expected: 0,
          actual: 0
        },
        redChest3: {
          expected: 0,
          actual: 0
        },
        blueChest3: {
          expected: 0,
          actual: 0
        },
        redChest4: {
          expected: 0,
          actual: 0
        },
        blueChest4: {
          expected: 0,
          actual: 0
        },
        redChest5: {
          expected: 0,
          actual: 0
        },
        blueChest5: {
          expected: 0,
          actual: 0
        },
        redHeadXS: {
          expected: 0,
          actual: 0
        },
        blueHeadXS: {
          expected: 0,
          actual: 0
        },
        redHeadS: {
          expected: 0,
          actual: 0
        },
        blueHeadS: {
          expected: 0,
          actual: 0
        },
        redHeadM: {
          expected: 0,
          actual: 0
        },
        blueHeadM: {
          expected: 0,
          actual: 0
        },
        redHeadL: {
          expected: 0,
          actual: 0
        },
        blueHeadL: {
          expected: 0,
          actual: 0
        },
        redHeadXL: {
          expected: 0,
          actual: 0
        },
        blueHeadXL: {
          expected: 0,
          actual: 0
        }
      };
      setChecklist(newChecklist);
      setIsSaved(false);
      setSignature('');
    } finally {
      setIsLoadingData(false);
    }
  };
  const updateCount = (field: keyof PrepChecklist, value: number) => {
    if (!checklist || isSaved) return;
    setChecklist({
      ...checklist,
      [field]: {
        ...checklist[field],
        actual: value
      }
    });
  };
  const confirmCount = (field: keyof PrepChecklist) => {
    if (!checklist || isSaved) return;
    setChecklist({
      ...checklist,
      [field]: {
        ...checklist[field],
        actual: checklist[field].expected
      }
    });
  };
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (isSaved) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ('touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left) * scaleX;
    const y = ('touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top) * scaleY;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || isSaved) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ('touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left) * scaleX;
    const y = ('touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top) * scaleY;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  };
  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignature(canvas.toDataURL());
    }
  };
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas && !isSaved) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    setSignature('');
  };
  const hasDiscrepancies = () => {
    if (!checklist) return false;
    return Object.values(checklist).some(item => item.expected !== item.actual);
  };
  const logActivity = async (action: string, details: string) => {
    try {
      const data = {
        appname: 'tkd-score',
        formname: 'Rental_Activity_Log',
        params: JSON.stringify({
          User: 'Admin',
          action_field: action,
          Category: 'onsiteprep',
          Details: details
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };
  const saveChecklist = async () => {
    if (!signature) {
      alert('Please provide your signature to confirm equipment count');
      return;
    }
    if (!checklist || !selectedEvent) {
      alert('No checklist data available');
      return;
    }
    setIsSaving(true);
    try {
      // Save equipment count to database
      const equipmentData = {
        Gen_3_Scoring_Rental: selectedEvent.id,
        Edit_Reason: existingRecordId && editReason ? editReason : '',
        Red_Chest_0: checklist.redChest0.actual,
        Red_Chest_1: checklist.redChest1.actual,
        Red_Chest_2: checklist.redChest2.actual,
        Red_Chest_3: checklist.redChest3.actual,
        Red_Chest_4: checklist.redChest4.actual,
        Red_Chest_5: checklist.redChest5.actual,
        Blue_Chest_0: checklist.blueChest0.actual,
        Blue_Chest_1: checklist.blueChest1.actual,
        Blue_Chest_2: checklist.blueChest2.actual,
        Blue_Chest_3: checklist.blueChest3.actual,
        Blue_Chest_4: checklist.blueChest4.actual,
        Blue_Chest_5: checklist.blueChest5.actual,
        Red_Head_XS: checklist.redHeadXS.actual,
        Red_Head_S: checklist.redHeadS.actual,
        Red_Head_M: checklist.redHeadM.actual,
        Red_Head_L: checklist.redHeadL.actual,
        Red_Head_XL: checklist.redHeadXL.actual,
        Blue_Head_XS: checklist.blueHeadXS.actual,
        Blue_Head_S: checklist.blueHeadS.actual,
        Blue_Head_M: checklist.blueHeadM.actual,
        Blue_Head_L: checklist.blueHeadL.actual,
        Blue_Head_XL: checklist.blueHeadXL.actual,
        Signature: signature
      };
      let data;
      if (existingRecordId) {
        // Update existing record
        data = {
          appname: 'tkd-score',
          reportname: 'Gen_3_Scoring_Equipment_Count_Report',
          id: existingRecordId,
          params: JSON.stringify(equipmentData)
        };
      } else {
        // Create new record
        data = {
          appname: 'tkd-score',
          formname: 'Gen_3_Scoring_Equipment_Count',
          params: JSON.stringify(equipmentData)
        };
      }
      const result = await callWFAPI(0, 'zohoCreator', data);
      // Store the ID if this was a new record
      if (!existingRecordId && result?.ID) {
        setExistingRecordId(result.ID);
      }
      // Build detailed checklist summary
      const checklistSummary = `Red/Blue Chest 0-5: ${checklist.redChest0.actual}/${checklist.blueChest0.actual}, ${checklist.redChest1.actual}/${checklist.blueChest1.actual}, ${checklist.redChest2.actual}/${checklist.blueChest2.actual}, ${checklist.redChest3.actual}/${checklist.blueChest3.actual}, ${checklist.redChest4.actual}/${checklist.blueChest4.actual}, ${checklist.redChest5.actual}/${checklist.blueChest5.actual}, Red/Blue Head XS-XL: ${checklist.redHeadXS.actual}/${checklist.blueHeadXS.actual}, ${checklist.redHeadS.actual}/${checklist.blueHeadS.actual}, ${checklist.redHeadM.actual}/${checklist.blueHeadM.actual}, ${checklist.redHeadL.actual}/${checklist.blueHeadL.actual}, ${checklist.redHeadXL.actual}/${checklist.blueHeadXL.actual}`;
      await logActivity('Onsite equipment count saved', `Saved onsite equipment count for ${selectedEvent.eventName}. Customer: ${selectedEvent.name}. Checklist: ${checklistSummary}. Signature captured. ${hasDiscrepancies() ? 'DISCREPANCIES DETECTED' : 'All counts match expected values'}`);
      setIsSaved(true);
      setEditReason('');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving equipment count:', error);
      alert('Failed to save equipment count. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleEdit = () => {
    setShowEditModal(true);
  };
  const confirmEdit = async () => {
    if (!editReason.trim()) {
      alert('Please provide a reason for editing');
      return;
    }
    await logActivity('Onsite equipment count edit initiated', `Editing onsite equipment count for ${selectedEvent?.eventName}. Reason: ${editReason}`);
    setIsSaved(false);
    clearSignature();
    setShowEditModal(false);
    setEditReason('');
  };
  const eligibleRentals = rentals.filter(rental => rental.invoiceId && rental.assignedTechnician);
  if (!selectedEvent) {
    const formatDateCompact = (dateString: string) => {
      const d = new Date(dateString + 'T00:00:00');
      return d.toLocaleString('en-US', {
        month: 'short'
      }) + ' ' + d.getDate();
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const filteredRentals = eligibleRentals.filter(rental => {
      if (!rental.proposedEventDate) return prepFilter === 'past';
      const eventDate = new Date(rental.proposedEventDate + 'T00:00:00');
      return prepFilter === 'future' ? eventDate >= today : eventDate < today;
    });
    const futureCount = eligibleRentals.filter(r => {
      if (!r.proposedEventDate) return false;
      return new Date(r.proposedEventDate + 'T00:00:00') >= today;
    }).length;
    const pastCount = eligibleRentals.length - futureCount;
    return <div data-id="element-1254">
        <div className="mb-4" data-id="element-1255">
          <h2 className="text-xl font-semibold text-slate-800 mb-1" data-id="element-1256">
            Onsite Prep - Equipment Verification
          </h2>
          <p className="text-sm text-slate-600" data-id="element-1257">
            Verify equipment counts with customer at event location
          </p>
        </div>

        <div className="flex gap-1.5 px-2 py-2 mb-2" data-id="element-1258">
          <button onClick={() => setPrepFilter('future')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${prepFilter === 'future' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1259">
            Future ({futureCount})
          </button>
          <button onClick={() => setPrepFilter('past')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${prepFilter === 'past' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1260">
            Past ({pastCount})
          </button>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-1261">
          {filteredRentals.map(rental => <div key={rental.id} onClick={() => initializeChecklist(rental)} className="py-2 px-2 cursor-pointer hover:bg-blue-50 transition-colors border-l-[3px] border-l-blue-500" data-id="element-1262">
              {/* Line 1: Date • Event Name • Rings */}
              <div className="flex items-center gap-1.5 min-w-0" data-id="element-1263">
                <span className="text-[11px] font-medium text-slate-500 flex-shrink-0 w-14" data-id="element-1264">
                  {rental.proposedEventDate ? formatDateCompact(rental.proposedEventDate) : '—'}
                </span>
                <span className="text-[12px] font-semibold text-slate-800 truncate flex-1" data-id="element-1265">
                  {rental.eventName}
                </span>
                {rental.selections.fullGen3Ring > 0 && <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-1 rounded flex-shrink-0" data-id="element-1266">
                    {rental.selections.fullGen3Ring}R
                  </span>}
              </div>
              {/* Line 2: Customer • Technician • Notes */}
              <div className="flex items-center gap-1.5 mt-0.5 min-w-0" data-id="element-1267">
                <span className="text-[10px] text-slate-500 truncate max-w-[120px]" data-id="element-1268">
                  {rental.name}
                </span>
                {rental.assignedTechnician && <>
                    <span className="text-[10px] text-slate-300" data-id="element-1269">•</span>
                    <span className="text-[10px] font-medium text-purple-600 truncate max-w-[100px]" data-id="element-1270">
                      {rental.assignedTechnician}
                    </span>
                  </>}
                <span className="flex-1" data-id="element-1271" />
                <div onClick={e => e.stopPropagation()} data-id="element-1272">
                  <InternalNotesButton rentalId={rental.id} initialNotes={rental.internalNotes} onNotesSaved={onNotesUpdated} data-id="element-1273" />
                </div>
              </div>
            </div>)}
        </div>

        {filteredRentals.length === 0 && <div className="text-center py-12" data-id="element-1274">
            <AlertTriangle size={48} className="text-slate-300 mx-auto mb-4" data-id="element-1275" />
            <p className="text-slate-600" data-id="element-1276">No events ready for onsite prep</p>
            <p className="text-sm text-slate-500 mt-1" data-id="element-1277">
              Events must be invoiced and have an assigned technician
            </p>
          </div>}
      </div>;
  }
  return <div data-id="element-1278">
      <button onClick={() => setSelectedEvent(null)} className="mb-4 flex items-center gap-2 text-blue-600 hover:text-blue-700" data-id="element-1279">
        <ArrowLeft size={18} data-id="element-1280" />
        Back to Events
      </button>

      <div className="bg-white border border-slate-200 rounded-lg p-6" data-id="element-1281">
        <div className="mb-6" data-id="element-1282">
          <h3 className="text-2xl font-bold text-slate-800 mb-3" data-id="element-1283">
            {selectedEvent.eventName}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-slate-600" data-id="element-1284">
            <div data-id="element-1285">
              <span className="font-medium text-slate-500" data-id="element-1286">Customer:</span>{' '}
              {selectedEvent.name}
            </div>
            <div data-id="element-1287">
              <span className="font-medium text-slate-500" data-id="element-1288">Technician:</span>{' '}
              {selectedEvent.assignedTechnician}
            </div>
            <div data-id="element-1289">
              <span className="font-medium text-slate-500" data-id="element-1290">Date:</span>{' '}
              {selectedEvent.proposedEventDate ? new Date(selectedEvent.proposedEventDate).toLocaleDateString() : 'TBD'}
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6" data-id="element-1291">
          {isLoadingData ? <div className="py-12 flex flex-col items-center justify-center" data-id="element-1292">
              <Loader2 size={48} className="animate-spin text-blue-600 mb-4" data-id="element-1293" />
              <p className="text-slate-600 font-medium" data-id="element-1294">
                Loading equipment data...
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1295">Please wait</p>
            </div> : <>
              <div className="flex items-center justify-between mb-4" data-id="element-1296">
                <h4 className="text-lg font-semibold text-slate-800" data-id="element-1297">
                  Equipment Count Verification
                </h4>
                {isSaved && <button onClick={handleEdit} className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium flex items-center gap-2" data-id="element-1298">
                    <Edit2 size={18} data-id="element-1299" />
                    Edit
                  </button>}
              </div>

              {checklist && <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-id="element-1300">
                    {(Object.keys(checklist) as Array<keyof PrepChecklist>).map(key => {
                const item = checklist[key];
                const matches = item.expected === item.actual;
                const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                // Determine color and icon
                const isRed = key.toLowerCase().includes('red');
                const isBlue = key.toLowerCase().includes('blue');
                const isHead = key.toLowerCase().includes('head');
                const isChest = key.toLowerCase().includes('chest');
                const colorClass = isRed ? 'text-red-600' : isBlue ? 'text-blue-600' : 'text-slate-600';
                const bgColorClass = isRed ? 'bg-red-50' : isBlue ? 'bg-blue-50' : 'bg-slate-50';
                const Icon = isHead ? Circle : Square;
                return <div key={key} className={`border-2 rounded-lg p-3 relative ${!matches && item.actual > 0 ? 'border-red-300 bg-red-50' : `border-slate-200 ${bgColorClass}`}`} data-id="element-1301">
                            {/* Status indicator in corner */}
                            <div className="absolute top-2 right-2" data-id="element-1302">
                              {matches && item.actual > 0 ? <div className="bg-green-500 rounded-full p-1" data-id="element-1303">
                                  <Check size={16} className="text-white" strokeWidth={3} data-id="element-1304" />
                                </div> : <div className="bg-red-500 rounded-full p-1" data-id="element-1305">
                                  <X size={16} className="text-white" strokeWidth={3} data-id="element-1306" />
                                </div>}
                            </div>

                            <label className={`block text-sm font-semibold mb-2 flex items-center gap-2 ${colorClass} pr-8`} data-id="element-1307">
                              <Icon size={16} className={colorClass} fill={isRed ? '#dc2626' : isBlue ? '#2563eb' : 'none'} data-id="element-1308" />
                              {label}
                            </label>

                            <div className="flex items-center gap-2" data-id="element-1309">
                              <div className="flex-1" data-id="element-1310">
                                <div className="flex items-center justify-between mb-1" data-id="element-1311">
                                  <div className="text-xs text-slate-500" data-id="element-1312">
                                    Expected: {item.expected}
                                  </div>
                                  {!isSaved && <button onClick={() => confirmCount(key)} className={`text-xs px-2 py-0.5 rounded transition-colors font-medium ${matches && item.actual > 0 ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-yellow-500 text-white hover:bg-yellow-600'}`} data-id="element-1313">
                                      {matches && item.actual > 0 ? 'Confirmed' : 'Confirm'}
                                    </button>}
                                </div>
                                <input type="number" min="0" value={item.actual} onChange={e => updateCount(key, parseInt(e.target.value) || 0)} onFocus={e => e.target.select()} disabled={isSaved} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed" data-id="element-1314" />
                              </div>
                              {!matches && item.actual > 0 && <AlertTriangle size={20} className="text-red-600 flex-shrink-0" data-id="element-1315" />}
                            </div>
                          </div>;
              })}
                  </div>

                  {hasDiscrepancies() && <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-id="element-1316">
                      <div className="flex items-start gap-2" data-id="element-1317">
                        <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-1318" />
                        <div data-id="element-1319">
                          <p className="text-sm font-semibold text-red-800 mb-1" data-id="element-1320">
                            Count Discrepancies Detected
                          </p>
                          <p className="text-xs text-red-700" data-id="element-1321">
                            Some equipment counts do not match expected values.
                            Please verify with customer before saving.
                          </p>
                        </div>
                      </div>
                    </div>}

                  <div className="border-t border-blue-300 pt-6" data-id="element-1322">
                    <div className="flex items-center justify-between mb-2" data-id="element-1323">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-1324">
                        Customer Signature{' '}
                        <span className="text-red-500" data-id="element-1325">*</span>
                      </label>
                      {!isSaved && signature && <button onClick={clearSignature} className="text-xs text-blue-600 hover:text-blue-700" data-id="element-1326">
                          Clear
                        </button>}
                    </div>
                    <p className="text-xs text-slate-600 mb-3" data-id="element-1327">
                      Customer confirms equipment count is accurate
                    </p>

                    {isSaved && signature ? <div className="border-2 border-green-300 rounded-lg bg-green-50 p-2" data-id="element-1328">
                        <img src={signature} alt="Customer Signature" className="w-full h-auto" data-id="element-1329" />
                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1" data-id="element-1330">
                          <CheckCircle size={12} data-id="element-1331" />
                          Equipment count verified and signed
                        </p>
                      </div> : <div className="border-2 border-slate-300 rounded-lg bg-white" data-id="element-1332">
                        <canvas ref={canvasRef} width={600} height={120} className="w-full touch-none cursor-crosshair" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} style={{
                  touchAction: 'none'
                }} data-id="element-1333" />
                      </div>}
                  </div>

                  {!isSaved && <div className="mt-6 flex gap-3" data-id="element-1334">
                      <button onClick={saveChecklist} disabled={!signature || isSaving} className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1335">
                        {isSaving ? <>
                            <Loader2 size={20} className="animate-spin" data-id="element-1336" />
                            Saving Equipment Count...
                          </> : 'Save Equipment Count'}
                      </button>
                      <button onClick={() => setSelectedEvent(null)} className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-1337">
                        Cancel
                      </button>
                    </div>}
                </>}
            </>}
        </div>
      </div>

      {/* Edit Reason Modal */}
      {showEditModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1338">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" data-id="element-1339">
            <div className="p-6 border-b border-slate-200" data-id="element-1340">
              <h3 className="text-xl font-bold text-slate-800" data-id="element-1341">
                Edit Equipment Count
              </h3>
            </div>
            <div className="p-6 space-y-4" data-id="element-1342">
              <p className="text-sm text-slate-600" data-id="element-1343">
                Please provide a reason for editing this equipment count
                verification.
              </p>
              <textarea value={editReason} onChange={e => setEditReason(e.target.value)} placeholder="Enter reason for editing..." rows={4} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-1344" />
              <div className="flex gap-3" data-id="element-1345">
                <button onClick={() => {
              setShowEditModal(false);
              setEditReason('');
            }} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1346">
                  Cancel
                </button>
                <button onClick={confirmEdit} disabled={!editReason.trim()} className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium disabled:bg-slate-300 disabled:cursor-not-allowed" data-id="element-1347">
                  Confirm Edit
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Success Modal */}
      {showSuccessModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1348">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden" data-id="element-1349">
            {/* Success Header with Gradient */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-center" data-id="element-1350">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4" data-id="element-1351">
                <CheckCircle className="text-green-600" size={32} data-id="element-1352" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1353">
                Equipment Count Saved!
              </h3>
              <div className="flex items-center justify-center gap-1 text-green-50" data-id="element-1354">
                <Sparkles size={16} data-id="element-1355" />
                <p className="text-sm" data-id="element-1356">Verified with customer signature</p>
                <Sparkles size={16} data-id="element-1357" />
              </div>
            </div>

            {/* Success Body */}
            <div className="p-6" data-id="element-1358">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-id="element-1359">
                <div className="space-y-2 text-sm" data-id="element-1360">
                  <div className="flex justify-between" data-id="element-1361">
                    <span className="text-green-700 font-medium" data-id="element-1362">Event:</span>
                    <span className="text-green-900 font-semibold" data-id="element-1363">
                      {selectedEvent?.eventName}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1364">
                    <span className="text-green-700 font-medium" data-id="element-1365">
                      Customer:
                    </span>
                    <span className="text-green-900" data-id="element-1366">
                      {selectedEvent?.name}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1367">
                    <span className="text-green-700 font-medium" data-id="element-1368">
                      Technician:
                    </span>
                    <span className="text-green-900" data-id="element-1369">
                      {selectedEvent?.assignedTechnician}
                    </span>
                  </div>
                </div>
              </div>

              {hasDiscrepancies() && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4" data-id="element-1370">
                  <div className="flex items-start gap-2" data-id="element-1371">
                    <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} data-id="element-1372" />
                    <div className="text-xs text-yellow-800" data-id="element-1373">
                      <p className="font-semibold mb-1" data-id="element-1374">Discrepancies Noted</p>
                      <p data-id="element-1375">
                        Some counts differ from expected values. This has been
                        logged for review.
                      </p>
                    </div>
                  </div>
                </div>}

              <div className="flex items-start gap-3 mb-6" data-id="element-1376">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} data-id="element-1377" />
                <div className="text-sm text-slate-600" data-id="element-1378">
                  <p className="font-medium text-slate-800 mb-1" data-id="element-1379">
                    Equipment Verified
                  </p>
                  <p data-id="element-1380">
                    All equipment counts have been verified and recorded with
                    customer signature. The event is ready to proceed.
                  </p>
                </div>
              </div>

              <button onClick={() => setShowSuccessModal(false)} className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold" data-id="element-1381">
                Continue
              </button>
            </div>
          </div>
        </div>}
    </div>;
}